import type { MetaReducer } from '@ngrx/store';
import { NGRX_STORAGE_KEYS } from '../../../../core/store/ngrx-persisted-state.keys';
import { createStorageSyncMetaReducer } from '../../../../core/store/ngrx-storage-sync.meta-reducer';
import { CompanyListFeatureState } from './company-list.models';
import { companyListFeature, initialCompanyListState } from './company-list.reducer';
import { getPaginationPage } from './company-list.cache';

function shouldClearFromStorage(state: CompanyListFeatureState): boolean {
  const filters = state.filters;
  const filtersDefault =
    (filters.companyName === null || filters.companyName === '') &&
    filters.cityId === null &&
    (filters.cityFilterLabel === null || filters.cityFilterLabel === '') &&
    filters.stateId === null &&
    filters.industryTypeId === null &&
    (filters.industryTypeFilterLabel === null || filters.industryTypeFilterLabel === '') &&
    filters.sourceId === null &&
    (filters.sourceFilterLabel === null || filters.sourceFilterLabel === '') &&
    filters.flameLevelId === null &&
    filters.isBlacklistCompany === false;

  const paginationDefault =
    state.pagination.firstRowOffset === 0 &&
    state.pagination.pageSize === initialCompanyListState.pagination.pageSize &&
    getPaginationPage(state.pagination) === 1 &&
    state.pagination.totalRecords === 0;

  const sortDefault =
    state.sort.sortField === null &&
    state.sort.sortOrder === 1 &&
    state.sort.previousSortField === '' &&
    state.sort.previousSortOrder === 0;

  const noGridData = state.gridData === null;

  return filtersDefault && paginationDefault && sortDefault && noGridData;
}

function parseCompanyListState(raw: unknown): CompanyListFeatureState | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const filters = record['filters'];
  const paginationRaw = record['pagination'];
  const sort = record['sort'];
  if (!filters || !paginationRaw || !sort || typeof filters !== 'object' || typeof paginationRaw !== 'object' || typeof sort !== 'object') {
    return null;
  }
  const partial = raw as Partial<CompanyListFeatureState>;
  if (!partial.filters || !partial.pagination || !partial.sort) {
    return null;
  }
  const paginationPartial = partial.pagination as CompanyListFeatureState['pagination'];
  const legacyGridData = record['currentPageData'];
  const rawGridData =
    partial.gridData ??
    (legacyGridData && typeof legacyGridData === 'object'
      ? (legacyGridData as CompanyListFeatureState['gridData'])
      : null);
  const legacyTotalRecords =
    rawGridData && typeof rawGridData === 'object' && 'totalRecords' in rawGridData
      ? Number((rawGridData as { totalRecords?: number }).totalRecords) || 0
      : null;
  const normalizedGridData =
    rawGridData && typeof rawGridData === 'object' && Array.isArray(rawGridData.companies)
      ? { companies: rawGridData.companies }
      : null;

  const parsedPagination: CompanyListFeatureState['pagination'] = {
    ...initialCompanyListState.pagination,
    ...paginationPartial,
    page: paginationPartial.page ?? getPaginationPage({
      ...initialCompanyListState.pagination,
      ...paginationPartial
    }),
    totalRecords: paginationPartial.totalRecords ?? legacyTotalRecords ?? 0
  };

  return {
    filters: { ...initialCompanyListState.filters, ...partial.filters },
    pagination: parsedPagination,
    sort: partial.sort as CompanyListFeatureState['sort'],
    gridData: normalizedGridData
  };
}

export const companyListMetaReducers: MetaReducer[] = [
  createStorageSyncMetaReducer<CompanyListFeatureState>({
    featureKey: companyListFeature.name,
    storageKey: NGRX_STORAGE_KEYS.companyList,
    shouldClearFromStorage,
    serialize: (state) => state,
    deserialize: parseCompanyListState
  })
];
